import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TransactionsHistory from "./components/TransactionsHistory";
import useCustomerDetail from "./../../features/customers/hooks/useCustomerDetail";
import CancelButton from "components/CancelButton";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";
import CustomTable from "../../components/CustomTable/CustomTable";
import PaginationControls from "../../components/PaginationControls/PaginationControls";
import OrderDrawer from "../../views/Order/components/OrderDrawer";
import StatusModal from "../../views/Order/components/StatusModal";
import DriverReminderModal from "../../views/Order/components/DriverReminderModal";

function CustomerDetail() {
  const { id } = useParams();
  console.log("customer id", id)
  const navigate = useNavigate();

  const {
    customer,
    orders,
    transactions,
    loading,
    error,
    resetAlerts,
  } = useCustomerDetail(id);

  const [activeTab, setActiveTab] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [addAmount, setAddAmount] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [success, setSuccess] = useState(null);

  const [currentPageForOrder, setCurrentPageForOrder] = useState(1);
  const [searchValueForOrder, setSearchValueForOrder] = useState("");
  const [totalPagesForOrder, setTotalPagesForOrder] = useState(1);
  const [customerOrders, setCustomerOrders] = useState([]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [driverReminderModalOpen, setDriverReminderModalOpen] = useState(false);
  const [statusForm, setStatusForm] = useState({ status_id: "", description: "" });
  const [driverReminderForm, setDriverReminderForm] = useState({ title: "", message: "" });
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [walletInfo, setWalletInfo] = useState(null);
  const [searchValueForTransaction, setSearchValueForTransaction] = useState("");
  const [currentPageForTransaction, setCurrentPageForTransaction] = useState(1);
  const [totalPagesForTransaction, setTotalPagesForTransaction] = useState(1);
  const [transactionsList, setTransactionsList] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const balance = customer?.wallet?.balance ?? customer?.wallet_balance ?? 0;
    setWalletBalance(Number(balance) || 0);
  }, [customer]);

  useEffect(() => {
    console.log("transactions", transactions)
    setWalletTransactions(transactions || []);
  }, [transactions]);

  const walletSummary = useMemo(() => {
    const wallet = customer?.wallet || {};
    const resolveEntry = (entry) => {
      if (!entry) return null;
      if (typeof entry === "number") {
        return { amount: entry, date: null };
      }
      return {
        amount: Number(entry.amount ?? entry.value ?? 0),
        date: entry.date || entry.created_at || entry.createdAt || null,
      };
    };

    return {
      balance: walletBalance,
      last_recharge: resolveEntry(wallet.last_recharge),
      last_spend: resolveEntry(wallet.last_spend),
    };
  }, [customer, walletBalance]);

  const recentOrders = useMemo(() => orders || [], [orders]);

  const handleCloseDialog = () => {
    setAddOpen(false);
    setAddAmount("");
  };

  const handleSnackbarClose = () => {
    resetAlerts();
    setSuccess(null);
  };

  const fetchOrdersList = async () => {
    try {
      setLoadingOrders(true);
      const params = {
        page: currentPageForOrder,
        searchValue: searchValueForOrder,
      };

      const response = await apiRequest.get(`${endPoints.GET_CUSTOMER_ORDERS}/${id}`, params);
      console.log("customer orders", response)

      if (response.success) {
        setCustomerOrders(response.data.items || []);
        const pagination = response.data.pagination;
        setCurrentPageForOrder(pagination?.current_page || 1);
        setTotalPagesForOrder(pagination?.total_pages || 1);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (id && activeTab === 1) {
      fetchOrdersList();
    }
  }, [id, activeTab, currentPageForOrder, searchValueForOrder]);

  const fetchSingleOrder = async (orderId) => {
    try {
      const response = await apiRequest.get(`${endPoints.GET_SINGLE_ORDER}/${orderId}`);
      if (response.success) {
        setSelectedOrder(response.data);
      }
    } catch (error) {
      console.error("Error fetching order detail:", error);
    }
  };

  const fetchOrderStatuses = async () => {
    try {
      const response = await apiRequest.get(endPoints.GET_ORDER_STATUSES, { limit: 0 });
      if (response.success) {
        setOrderStatuses(response.data.items);
      }
    } catch (error) {
      console.error("Error fetching order statuses:", error);
    }
  };

  useEffect(() => {
    fetchOrderStatuses();
  }, []);

  const handleDetailClick = (order) => {
    fetchSingleOrder(order.id);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedOrder(null);
  };

  const handleDriverReminderChange = (e) => {
    const { name, value } = e.target;
    setDriverReminderForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDriverReminderModal = () => {
    setDriverReminderModalOpen(true);
    setDriverReminderForm({ title: "", message: "" });
  };

  const handleStatusModal = () => {
    setStatusModalOpen(true);
    setStatusForm({ status_id: "", description: "" });
  };

  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    setStatusForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendDriverReminder = async () => {
    if (!driverReminderForm.title || !driverReminderForm.message) {
      setSuccess("Title and message are required.");
      return;
    }
    const formData = {
      order_id: selectedOrder?.id,
      title: driverReminderForm.title,
      description: driverReminderForm.message,
    };

    const response = await apiRequest.post(endPoints.SEND_DRIVER_REMINDER, formData);
    if (response.success) {
      setDriverReminderModalOpen(false);
      setSuccess("Driver reminder sent successfully.");
    }
  };

  const handleSaveStatus = async () => {
    if (!statusForm.status_id) {
      setSuccess("Please select a status.");
      return;
    }
    const formData = {
      status_id: statusForm.status_id,
      order_id: selectedOrder?.id,
      description: statusForm.description,
    };
    const response = await apiRequest.post(endPoints.CHANGE_ORDER_STATUS, formData);
    if (response.success) {
      fetchSingleOrder(selectedOrder?.id);
      setStatusModalOpen(false);
      setSuccess("Order status updated successfully.");
      fetchOrdersList();
    }
  };

  const orderColumns = [
    { key: "order_ref", label: "Order Ref" },
    {
      key: "user_id",
      label: "Customer",
      render: (value, row) => row.user?.full_name ?? row.user_id,
    },
    {
      key: "driver_id",
      label: "Partner",
      render: (value, row) => {
        const driverName = row.driver?.full_name || row.driver_id || "N/A";
        const businessName = row.business?.name;
        return businessName ? `${driverName} (${businessName})` : driverName;
      },
    },
    {
      key: "category_id",
      label: "Category",
      render: (value, row) => row.category?.name_en ?? row.category_id,
    },
    {
      key: "service_type_id",
      label: "Service Type",
      render: (value, row) => row.service_type?.name_en ?? row.service_type_id,
    },
    {
      key: "current_status_id",
      label: "Current Status",
      render: (value, row) => row.current_order_status?.name ?? row.current_status_id,
    },
  ];

  const walletColumns = [
    { key: "type", label: "Type" },
    { key: "amount", label: "Amount", render: (val) => formatCurrency(val) },
    { key: "description", label: "Description" },
    {
      key: "createdAt",
      label: "Created At",
      render: (value) => new Date(value).toLocaleString(),
    },
  ];

  const fetchWalletDetails = async () => {
    try {
      const response = await apiRequest.get(`${endPoints.GET_WALLET_BY_USER_ID}/${id}`);
      if (response.success) {
        setWalletInfo(response.data);
      }
    } catch (error) {
      console.error("Error fetching wallet details:", error);
    }
  };

  const fetchWalletTransactions = async () => {
    if (!walletInfo?.id) return;
    try {
      setLoadingTransactions(true);
      const params = {
        page: currentPageForTransaction,
        searchValue: searchValueForTransaction,
      };
      const response = await apiRequest.get(`${endPoints.GET_TRANSACTIONS_BY_WALLET_ID}/${walletInfo.id}`, params);
      if (response.success) {
        setTransactionsList(response.data.items || []);
        const pagination = response.data.pagination;
        setCurrentPageForTransaction(pagination?.current_page || 1);
        setTotalPagesForTransaction(pagination?.total_pages || 1);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchWalletDetails();
    }
  }, [id]);

  useEffect(() => {
    if (walletInfo?.id && activeTab === 2) {
      fetchWalletTransactions();
    }
  }, [walletInfo?.id, activeTab, currentPageForTransaction, searchValueForTransaction]);



  const displayCustomer = customer || {};
  const joinedAt = displayCustomer.joinedAt || displayCustomer.createdAt || displayCustomer.created_at;
  const totalOrders = displayCustomer.total_orders ?? displayCustomer.orders_count ?? recentOrders.length ?? 0;
  const lastOrderDate = recentOrders[0]?.created_at || recentOrders[0]?.createdAt || recentOrders[0]?.date;
  const statusLabel = (displayCustomer.status || "unknown").replace(/_/g, " ");

  const formatEntry = (entry) => {
    if (!entry) return "No data";
    const amount = Number(entry.amount ?? 0).toFixed(2);
    const date = entry.date ? new Date(entry.date).toLocaleString() : "";
    return `$${amount}${date ? ` on ${date}` : ""}`;
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return "$0.00";
    return `$${numericValue.toFixed(2)}`;
  };

  const InfoRow = ({ label, value }) => (
    <MDBox>
      <MDTypography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
        {label}
      </MDTypography>
      <MDTypography variant="body1" fontWeight={500}>
        {value || "-"}
      </MDTypography>
    </MDBox>
  );

  const heroStats = [
    { label: "Wallet Balance", value: formatCurrency(walletSummary.balance) },
    {
      label: "Last Transaction",
      value: walletSummary.last_spend?.date
        ? new Date(walletSummary.last_spend.date).toLocaleDateString()
        : walletSummary.last_recharge?.date
          ? new Date(walletSummary.last_recharge.date).toLocaleDateString()
          : "No activity",
    },
    { label: "Orders", value: totalOrders },
    {
      label: "Last Order",
      value: lastOrderDate ? new Date(lastOrderDate).toLocaleDateString() : "No orders",
    },
  ];

  const profileDetails = [
    { label: "Email", value: displayCustomer.email },
    { label: "Phone", value: displayCustomer.contact || displayCustomer.phone },
    { label: "Joined", value: joinedAt ? new Date(joinedAt).toLocaleDateString() : "-" },
    { label: "Address", value: displayCustomer.address },
  ];

  const walletDetails = [
    { label: "Balance", value: formatCurrency(walletSummary.balance) },
    { label: "Last Recharge", value: formatEntry(walletSummary.last_recharge) },
    { label: "Last Spend", value: formatEntry(walletSummary.last_spend) },
  ];

  const initials = displayCustomer.full_name
    ? displayCustomer.full_name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase()
    : "CU";

  const avatarSrc = displayCustomer.profile
    ? `${apiRequest.BASE_URL}/${displayCustomer.profile}`
    : undefined;

  const tabConfig = [
    { label: "Profile Info", header: "Profile Information" },
    { label: "Orders Info", header: "Orders Information" },
    { label: "Transactions Info", header: "Transactions Information" },
  ];

  const tabHeaders = tabConfig.map((tab) => tab.header);

  const lastTransaction = walletTransactions?.[0];
  const lastTransactionType = (lastTransaction?.type || "").toLowerCase();
  const lastTransactionAmount = lastTransaction?.amount || lastTransaction?.value;
  const lastTransactionDate = lastTransaction?.date || lastTransaction?.created_at || lastTransaction?.createdAt;

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 0: {
        const profileSections = [
          {
            title: "Customer Profile",
            description: "Primary contact and identity",
            items: [
              { label: "Full Name", value: displayCustomer.full_name },
              { label: "Email", value: displayCustomer.email },
              { label: "Phone", value: displayCustomer.contact || displayCustomer.phone },
              { label: "Address", value: displayCustomer.address },
            ],
            photo: avatarSrc,
          },
          {
            title: "Account Snapshot",
            description: "Platform activity overview",
            items: [
              { label: "Status", value: statusLabel },
              { label: "Total Orders", value: totalOrders },
              { label: "Joined", value: joinedAt ? new Date(joinedAt).toLocaleDateString() : "-" },
              { label: "Wallet Balance", value: formatCurrency(walletSummary.balance) },
            ],
          },
        ];
        return (
          <Grid container spacing={3}>
            {profileSections.map((section) => (
              <Grid item xs={12} md={6} key={section.title}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    height: "100%",
                    p: 3,
                    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                  }}
                >
                  <MDTypography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    {section.title}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={2}>
                    {section.description}
                  </MDTypography>
                  <Grid container spacing={2}>
                    {section.items.map((detail) => (
                      <Grid item xs={12} key={detail.label}>
                        <InfoRow {...detail} />
                      </Grid>
                    ))}
                  </Grid>
                  {section.photo && (
                    <MDBox mt={3}>
                      <MDTypography variant="caption" color="text.secondary" display="block" mb={1}>
                        Profile Photo
                      </MDTypography>
                      <Avatar
                        src={section.photo}
                        alt={displayCustomer.full_name}
                        sx={{
                          width: 96,
                          height: 96,
                          borderRadius: 3,
                          boxShadow: "0 4px 12px rgba(15,23,42,0.15)",
                        }}
                      />
                    </MDBox>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        );
      }
      case 1:
        return (
          <>
            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
              <TextField
                label="Search"
                variant="outlined"
                placeholder="Search orders"
                value={searchValueForOrder}
                onChange={(e) => {
                  setCurrentPageForOrder(1);
                  setSearchValueForOrder(e.target.value);
                }}
                sx={{ minWidth: { xs: "100%", sm: 240 } }}
              />
            </MDBox>
            <CustomTable
              data={customerOrders || []}
              columns={orderColumns}
              onViewDetails={handleDetailClick}
              loading={loadingOrders}
            />
            <PaginationControls
              currentPage={currentPageForOrder}
              totalPages={totalPagesForOrder}
              onPageChange={setCurrentPageForOrder}
            />
          </>
        );
      case 2:
        return (
          <>
            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
              <TextField
                label="Search"
                variant="outlined"
                placeholder="Search transactions"
                value={searchValueForTransaction}
                onChange={(e) => {
                  setCurrentPageForTransaction(1);
                  setSearchValueForTransaction(e.target.value);
                }}
                sx={{ minWidth: { xs: "100%", sm: 240 } }}
              />
            </MDBox>
            <CustomTable data={transactionsList || []} columns={walletColumns} loading={loadingTransactions} />
            <PaginationControls
              currentPage={currentPageForTransaction}
              totalPages={totalPagesForTransaction}
              onPageChange={setCurrentPageForTransaction}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3} px={2}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDTypography variant="h4">Customer Detail</MDTypography>
          <CancelButton
            onClick={() => navigate(-1)}
            sx={{ color: "#000 !important", border: "1px solid #45ce23ff !important" }}
          >
            Back
          </CancelButton>
        </MDBox>

        {!displayCustomer?.id && !loading ? (
          <Card sx={{ p: 3 }}>
            <MDTypography variant="body1">Customer information could not be found.</MDTypography>
          </Card>
        ) : (
          <>
            <Card
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 3,
                background: "linear-gradient(135deg, #d3f8df 0%, #f4fff7 100%)",
                boxShadow: 6,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar
                    sx={{
                      bgcolor: "#0f172a",
                      width: 72,
                      height: 72,
                      fontSize: 32,
                      fontWeight: 600,
                    }}
                    src={avatarSrc}
                  >
                    {initials}
                  </Avatar>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDTypography variant="h5" fontWeight={700}>
                    {displayCustomer.full_name || "Unnamed Customer"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Snapshot of orders and wallet
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    {heroStats.map((stat) => (
                      <Grid item xs={6} sm={3} key={stat.label}>
                        <Card
                          sx={{
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: "rgba(255,255,255,0.75)",
                            boxShadow: "0 4px 10px rgba(15,23,42,0.08)",
                          }}
                        >
                          <MDTypography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                            {stat.label}
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight={700}>
                            {stat.value}
                          </MDTypography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Card>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card sx={{ p: 2 }}>
                  <Tabs
                    orientation="vertical"
                    value={activeTab}
                    onChange={(_e, newValue) => setActiveTab(newValue)}
                    sx={{
                      borderRight: 1,
                      borderColor: "divider",
                      "& .MuiTab-root": {
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        gap: 1,
                        fontWeight: 500,
                      },
                    }}
                  >
                    {tabConfig.map((tab) => (
                      <Tab key={tab.label} label={tab.label} />
                    ))}
                  </Tabs>
                </Card>
              </Grid>

              <Grid item xs={12} md={9}>
                <Card sx={{ p: { xs: 2, md: 3 }, minHeight: 400 }}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={1}
                    mb={2}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <MDTypography variant="h6">{tabHeaders[activeTab]}</MDTypography>
                      {activeTab === 0 && (
                        <Chip
                          label={statusLabel}
                          size="small"
                          color={statusLabel.toLowerCase().includes("active") ? "success" : "default"}
                          sx={{ textTransform: "capitalize" }}
                        />
                      )}
                    </Stack>
                    {activeTab === 0 && (
                      <MDTypography variant="body2" color="text.secondary">
                        Joined {joinedAt ? new Date(joinedAt).toLocaleDateString() : "-"}
                      </MDTypography>
                    )}
                  </MDBox>

                  {renderActiveTabContent()}
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </MDBox>
      <Footer />



      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={error ? "error" : "success"} sx={{ width: "100%" }}>
          {error || success}
        </Alert>
      </Snackbar>

      <OrderDrawer
        isDrawerOpen={isDrawerOpen}
        isMobile={isMobile}
        selectedOrder={selectedOrder}
        handleCloseDrawer={handleCloseDrawer}
        handleStatusModal={handleStatusModal}
        handleDriverReminderModal={handleDriverReminderModal}
      />

      <StatusModal
        statusModalOpen={statusModalOpen}
        setStatusModalOpen={setStatusModalOpen}
        handleSaveStatus={handleSaveStatus}
        statusForm={statusForm}
        handleStatusChange={handleStatusChange}
        orderStatuses={orderStatuses}
      />

      <DriverReminderModal
        driverReminderModalOpen={driverReminderModalOpen}
        setDriverReminderModalOpen={setDriverReminderModalOpen}
        handleSendDriverReminder={handleSendDriverReminder}
        driverReminderForm={driverReminderForm}
        handleDriverReminderChange={handleDriverReminderChange}
      />
    </DashboardLayout>
  );
}

export default CustomerDetail;
